<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import { subDays, fromUnixTime } from 'date-fns';
import { getUnixStartOfDay, getUnixEndOfDay } from 'helpers/DateHelper';
import {
  buildFilterList,
  buildRatingsList,
  getFilterType,
} from './CsatFilterHelpers';
import CsatFilterModal from './CsatFilterModal.vue';
import WootDatePicker from 'dashboard/components/ui/DatePicker/DatePicker.vue';
import {
  parseReportURLParams,
  parseFilterURLParams,
  generateCompleteURLParams,
} from '../../helpers/reportFilterHelper';
import { DATE_RANGE_TYPES } from 'dashboard/components/ui/DatePicker/helpers/DatePickerHelper';

const props = defineProps({
  showTeamFilter: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['filterChange']);

const { t } = useI18n();
const store = useStore();
const route = useRoute();
const router = useRouter();

// Initialize from URL params immediately
const urlParams = parseReportURLParams(route.query);
const urlFilters = parseFilterURLParams(route.query);

const initialDateRange =
  urlParams.from && urlParams.to
    ? [fromUnixTime(urlParams.from), fromUnixTime(urlParams.to)]
    : [subDays(new Date(), 6), new Date()];

const showFilterModal = ref(false);
const customDateRange = ref(initialDateRange);
const selectedDateRange = ref(urlParams.range || DATE_RANGE_TYPES.LAST_7_DAYS);

const appliedFilters = ref({
  user_ids: urlFilters.agent_id || [],
  inbox_id: urlFilters.inbox_id || [],
  team_id: urlFilters.team_id || [],
  rating: urlFilters.rating || [],
});

const agents = computed(() => store.getters['agents/getAgents']);
const inboxes = computed(() => store.getters['inboxes/getInboxes']);
const teams = computed(() => store.getters['teams/getTeams']);
const ratings = computed(() => buildRatingsList(t));

const from = computed(() => getUnixStartOfDay(customDateRange.value[0]));
const to = computed(() => getUnixEndOfDay(customDateRange.value[1]));

const hasActiveFilters = computed(() =>
  Object.values(appliedFilters.value).some(
    value => Array.isArray(value) && value.length > 0
  )
);

const selectedIdsMap = computed(() =>
  Object.entries(appliedFilters.value).reduce((map, [key, value]) => {
    map[getFilterType(key, 'keyToType')] = value;
    return map;
  }, {})
);

const getFilterSource = type => {
  const sources = {
    agents: agents.value,
    inboxes: inboxes.value,
    teams: teams.value,
    ratings: ratings.value,
  };
  return sources[type] || [];
};

const getFilterOptions = type => {
  if (type === 'ratings') return ratings.value;
  return buildFilterList(getFilterSource(type), type);
};

const modalFilters = computed(() => {
  const filterTypes = [
    {
      id: '1',
      name: t('CSAT_REPORTS.FILTERS.AGENTS.LABEL'),
      type: 'agents',
    },
    {
      id: '2',
      name: t('CSAT_REPORTS.FILTERS.INBOXES.LABEL'),
      type: 'inboxes',
    },
    {
      id: '3',
      name: t('CSAT_REPORTS.FILTERS.RATINGS.LABEL'),
      type: 'ratings',
    },
  ];

  if (props.showTeamFilter) {
    filterTypes.splice(2, 0, {
      id: '4',
      name: t('CSAT_REPORTS.FILTERS.TEAMS.LABEL'),
      type: 'teams',
    });
  }

  return filterTypes.map(filter => ({
    ...filter,
    options: getFilterOptions(filter.type),
    selectedIds: selectedIdsMap.value[filter.type] || [],
  }));
});

const updateURLParams = () => {
  const params = generateCompleteURLParams({
    from: from.value,
    to: to.value,
    range: selectedDateRange.value,
    filters: {
      agent_id: appliedFilters.value.user_ids,
      inbox_id: appliedFilters.value.inbox_id,
      team_id: appliedFilters.value.team_id,
      rating: appliedFilters.value.rating,
    },
  });
  router.replace({ query: params });
};

const emitChange = () => {
  updateURLParams();
  emit('filterChange', {
    from: from.value,
    to: to.value,
    selectedAgents: appliedFilters.value.user_ids.map(id => ({ id })),
    selectedInbox: appliedFilters.value.inbox_id.map(id => ({ id })),
    selectedTeam: appliedFilters.value.team_id.map(id => ({ id })),
    selectedRating: appliedFilters.value.rating.map(id => ({ value: id })),
  });
};

const setInitialRange = () => {
  if (!urlParams.from || !urlParams.to) {
    const fromDate = subDays(new Date(), 6);
    const toDate = new Date();
    customDateRange.value = [fromDate, toDate];
  }
  emitChange();
};

const activeFilterCount = computed(() =>
  Object.values(appliedFilters.value).reduce(
    (sum, arr) => sum + (Array.isArray(arr) ? arr.length : 0),
    0
  )
);

const addFilter = item => {
  const { type, id } = item;
  const filterKey = getFilterType(type, 'typeToKey');
  const current = appliedFilters.value[filterKey] || [];

  appliedFilters.value[filterKey] = current.includes(id)
    ? current.filter(existingId => existingId !== id)
    : [...current, id];

  emitChange();
};

const clearGroup = type => {
  const filterKey = getFilterType(type, 'typeToKey');
  appliedFilters.value[filterKey] = [];
  emitChange();
};

const selectGroup = type => {
  const filterKey = getFilterType(type, 'typeToKey');
  const options = getFilterOptions(type);
  appliedFilters.value[filterKey] = options.map(opt => opt.id);
  emitChange();
};

const clearAllFilters = () => {
  appliedFilters.value = {
    user_ids: [],
    inbox_id: [],
    team_id: [],
    rating: [],
  };
  emitChange();
};

const onDateRangeChange = value => {
  const [startDate, endDate, rangeType] = value;
  customDateRange.value = [startDate, endDate];
  selectedDateRange.value = rangeType || DATE_RANGE_TYPES.CUSTOM_RANGE;
  emitChange();
};

onMounted(() => {
  setInitialRange();
});
</script>

<template>
  <div class="flex flex-col flex-wrap w-full gap-3 md:flex-row">
    <WootDatePicker
      v-model:date-range="customDateRange"
      v-model:range-type="selectedDateRange"
      @date-range-changed="onDateRangeChange"
    />

    <div class="flex items-baseline gap-2">
      <button
        type="button"
        class="relative inline-flex items-center gap-2 px-3 py-2 text-sm rounded-lg border border-n-slate-7 bg-n-alpha-1 hover:border-n-slate-9"
        @click="showFilterModal = true"
      >
        {{ $t('CSAT_REPORTS.FILTERS.ADD_FILTER') }}

        <span
          v-if="activeFilterCount > 0"
          class="absolute -top-2 -right-2 min-w-[18px] h-[18px] px-1 flex items-center justify-center text-[11px] font-semibold rounded-full bg-woot-500 text-white"
        >
          {{ activeFilterCount }}
        </span>
      </button>

      <button
        v-if="hasActiveFilters"
        type="button"
        class="px-3 py-2 text-sm text-n-slate-11 hover:text-n-slate-12 hover:underline"
        @click="clearAllFilters"
      >
        {{ $t('CSAT_REPORTS.FILTERS.CLEAR_ALL') }}
      </button>

      <CsatFilterModal
        :show="showFilterModal"
        :filters="modalFilters"
        @close="showFilterModal = false"
        @add-filter="addFilter"
        @clear-group="clearGroup"
        @select-group="selectGroup"
        @clear="clearAllFilters"
      />
    </div>
  </div>
</template>
